import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const popupText = {
  de: {
    title: 'Newsletter',
    description: 'Ab und zu teilen wir Tipps zu Marketing, Webseiten, Sichtbarkeit und Branding — verständlich erklärt und direkt anwendbar. Kein Blabla. Kein tägliches Bombardement.',
    placeholder: 'Ihre E-Mail-Adresse',
    button: 'Anmelden',
    success: 'Vielen Dank für Ihre Anmeldung!',
    error: 'Ein Fehler ist aufgetreten.',
    close: 'Schliessen'
  },
  it: {
    title: 'Newsletter',
    description: 'Di tanto in tanto condividiamo consigli su marketing, siti web, visibilità e branding — spiegati in modo semplice. Niente chiacchiere. Niente spam quotidiano.',
    placeholder: 'Il vostro indirizzo e-mail',
    button: 'Iscriviti',
    success: "Grazie per l'iscrizione!",
    error: 'Si è verificato un errore.',
    close: 'Chiudi'
  },
  en: {
    title: 'Newsletter',
    description: 'From time to time we share tips on marketing, websites, visibility and branding — explained simply. No blabla. No daily bombardment.',
    placeholder: 'Your email address',
    button: 'Subscribe',
    success: 'Thank you for subscribing!',
    error: 'An error occurred.',
    close: 'Close'
  },
  es: {
    title: 'Newsletter',
    description: 'De vez en cuando compartimos consejos sobre marketing, sitios web, visibilidad y branding — explicados de forma sencilla. Sin bla bla. Sin bombardeo diario.',
    placeholder: 'Su correo electrónico',
    button: 'Suscribirse',
    success: '¡Gracias por suscribirse!',
    error: 'Ocurrió un error.',
    close: 'Cerrar'
  }
};

export default function NewsletterPopup() {
  const { language } = useLanguage();
  const text = popupText[language] || popupText.de;
  
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    // Check if popup was already shown in this session
    const popupShown = sessionStorage.getItem('newsletterPopupShown');
    
    if (!popupShown) {
      // Show popup after 8 seconds
      const timer = setTimeout(() => {
        setIsOpen(true);
        sessionStorage.setItem('newsletterPopupShown', 'true');
      }, 8000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || isSubmitting) return;
    
    setIsSubmitting(true);
    setStatus(null);
    
    try {
      await axios.post(`${API}/newsletter`, { email, language });
      setStatus({ type: 'success', message: text.success });
      setEmail('');
      setTimeout(() => setIsOpen(false), 2000);
    } catch (error) {
      setStatus({ type: 'error', message: text.error });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 z-50"
          />
          
          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed top-1/2 right-8 md:right-16 -translate-y-1/2 z-50 w-[90%] max-w-md"
          >
            <div className="bg-[#6D28D9] p-6 md:p-8 rounded-2xl shadow-2xl relative">
              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                data-testid="newsletter-popup-close"
              >
                <X className="w-5 h-5" />
              </button>
              
              {/* Content */}
              <h3 className="text-2xl font-bold text-white font-['Manrope'] mb-3">
                {text.title}
              </h3>
              <p className="text-white/90 text-sm mb-6 leading-relaxed">
                {text.description}
              </p>
              
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={text.placeholder}
                  className="w-full bg-white/20 border border-white/30 rounded-lg px-4 py-3 text-white placeholder:text-white/80 focus:outline-none focus:border-white transition-colors"
                  data-testid="newsletter-popup-email"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0a0a0a] text-white rounded-lg px-6 py-3 font-medium hover:bg-[#1a1a1a] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="newsletter-popup-submit"
                  aria-label="Newsletter abonnieren"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {text.button}
                    </>
                  )}
                </button>
              </form>
              
              {/* Status message */}
              {status && (
                <p className={`mt-4 text-sm text-center ${status.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                  {status.message}
                </p>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
